import { PasswordInput } from '@/components/blocks/PasswordInput';
import { passwordSx } from '@/components/content/Account/styles/password';
import { buttonSX } from '@/components/content/ForgotPassword/styles/container';
import { emailImageSX } from '@/components/content/ForgotPassword/styles/sendCode';
import { useForgotPassword } from '@/data/Content/ForgotPassword';
import { useNotifications } from '@/data/Content/Notifications';
import { useLocalization } from '@/data/Localization';
import MailSharpIcon from '@mui/icons-material/MailSharp';
import { Box, Button, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { BULLET_POINT } from 'shared/custom/constants/commonConstants';

const INVALID_CODE_ERROR = '2190';
const EXPIRED_CODE_ERROR = '2195';
const PASSWORD_REUSED_ERROR = '2260';
const MAX_CONSECUTIVE_CHAR_ERROR = '2210';
const CODE_MAX_LENGTH = 6;

const specialCharacters = /[-=_*?!@#$/(){}.,;:]/;
const upperCaseLetters = /[A-ZÑ]/;
const lowerCaseLetters = /[a-zñ]/;
const anyNumber = /\d/;

type PasswordValidations = {
	min8Chars: boolean | null;
	lowerCase: boolean | null;
	upperCase: boolean | null;
	number: boolean | null;
	specialChar: boolean | null;
};

const initialValidations: PasswordValidations = {
	min8Chars: null,
	lowerCase: null,
	upperCase: null,
	number: null,
	specialChar: null,
};

type Props = {
	email: string;
	onSuccess: () => void;
	onResend: () => void;
};

export const SendForgotPasswordCode = ({ email, onSuccess, onResend }: Props) => {
	const nls = useLocalization('ForgotPassword');
	const RegistrationNLS = useLocalization('RegistrationLayout');
	const { resetPasswordWithCodeSubmit, sendPasswordSavedEmail } = useForgotPassword();
	const { showMessage } = useNotifications();

	const [code, setCode] = useState('');
	const [codeError, setCodeError] = useState('');
	const [logonPassword, setLogonPassword] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [logonPasswordVerify, setLogonPasswordVerify] = useState('');
	const [passwordVerifyError, setPasswordVerifyError] = useState('');
	const [passwordValidations, setPasswordValidations] =
		useState<PasswordValidations>(initialValidations);
	const [loading, setLoading] = useState(false);

	const isCodeValid = code.trim().length === CODE_MAX_LENGTH;
	const isPasswordValid =
		passwordValidations.min8Chars === true &&
		passwordValidations.lowerCase === true &&
		passwordValidations.upperCase === true &&
		passwordValidations.number === true &&
		passwordValidations.specialChar === true;
	const isFormReady =
		isCodeValid &&
		isPasswordValid &&
		logonPassword === logonPasswordVerify &&
		logonPasswordVerify.length > 0;

	const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value.trim();
		if (/^\d*$/.test(val) && val.length <= CODE_MAX_LENGTH) {
			setCode(val);
			setCodeError('');
		}
	};

	const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setLogonPassword(val);
		setPasswordError('');
		setPasswordValidations({
			min8Chars: val.length >= 8,
			lowerCase: lowerCaseLetters.test(val),
			upperCase: upperCaseLetters.test(val),
			number: anyNumber.test(val),
			specialChar: specialCharacters.test(val),
		});
	};

	const handlePasswordVerifyChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setLogonPasswordVerify(val);
		if (val.length > 0 && logonPassword !== val) {
			setPasswordVerifyError(nls.Msgs.NoMatchPasswords.t());
		} else {
			setPasswordVerifyError('');
		}
	};

	const handlePasswordBlur = () => {
		if (!isPasswordValid && logonPassword.length > 0) {
			setPasswordError(RegistrationNLS.Msgs.NoFullRequirementsPassword.t());
		}
	};

	const getValidationSx = (value: boolean | null) => {
		if (value === null) return passwordSx.regular;
		return value ? passwordSx.success : passwordSx.error;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isFormReady) return;
		setLoading(true);
		const result = await resetPasswordWithCodeSubmit({
			email,
			code,
			logonPassword,
			logonPasswordVerify,
		});
		setLoading(false);
		if (result.success) {
			sendPasswordSavedEmail(email);
			onSuccess();
			window.scrollTo(0, 0);
		} else if (result.errorCode === INVALID_CODE_ERROR) {
			setCodeError(nls.Msgs.InvalidCode.t());
		} else if (result.errorCode === EXPIRED_CODE_ERROR) {
			setCodeError(nls.Msgs.ExpiredCode.t());
		} else if (result.errorCode === PASSWORD_REUSED_ERROR) {
			setPasswordError(nls.Msgs.PasswordReused.t());
		} else if (result.errorCode === MAX_CONSECUTIVE_CHAR_ERROR) {
			showMessage(nls.Msgs.MaxConsecutiveChar.t(), { isGeneric: true });
		}
	};

	return (
		<Stack component="form" noValidate onSubmit={handleSubmit} spacing={2}>
			{/* Subtitle + image + reminder */}
			<Typography variant="body1" color="grey.500">
				{nls.NewPasswordContentText.t()}
			</Typography>

			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Box component="img" src="/HDMX/images/send-mail.svg" alt="" sx={emailImageSX} />
			</Box>

			<Typography variant="body1" color="grey.500">
				{nls.VerificationCodeContentText.t()}
			</Typography>

			<Typography variant="bodySubText" color="grey.400">
				{nls.VerificationCodeReminder.t()}
			</Typography>

			{/* Email display with icon */}
			<TextField
				fullWidth
				disabled
				value={email}
				variant="filled"
				hiddenLabel
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<MailSharpIcon fontSize="small" />
						</InputAdornment>
					),
				}}
			/>

			{/* Verification code */}
			<TextField
				fullWidth
				required
				name="verificationCode"
				label={RegistrationNLS.VerificationToken.t()}
				value={code}
				onChange={handleCodeChange}
				inputProps={{ maxLength: CODE_MAX_LENGTH, inputMode: 'numeric' }}
				error={!!codeError}
				helperText={codeError}
				autoFocus
				size="small"
			/>

			{/* Password requirements checklist */}
			<Stack spacing={0.5}>
				<Typography variant="body1" sx={passwordSx.requirementsTitle}>
					{RegistrationNLS.RegistrationPasswordValidation.t()}
				</Typography>
				<Box sx={passwordSx.requirementsContainer}>
					<Grid display="grid" md={12} lg={6}>
						<Typography variant="bodySubText" sx={getValidationSx(passwordValidations.min8Chars)}>
							{BULLET_POINT}
							{RegistrationNLS.PasswordCriteria.Criteria1.t()}
						</Typography>
						<Typography variant="bodySubText" sx={getValidationSx(passwordValidations.lowerCase)}>
							{BULLET_POINT}
							{RegistrationNLS.PasswordCriteria.Criteria2.t()}
						</Typography>
						<Typography variant="bodySubText" sx={getValidationSx(passwordValidations.upperCase)}>
							{BULLET_POINT}
							{RegistrationNLS.PasswordCriteria.Criteria3.t()}
						</Typography>
					</Grid>
					<Grid display="grid" md={12} lg={6}>
						<Typography variant="bodySubText" sx={getValidationSx(passwordValidations.number)}>
							{BULLET_POINT}
							{RegistrationNLS.PasswordCriteria.Criteria4.t()}
						</Typography>
						<Typography variant="bodySubText" sx={getValidationSx(passwordValidations.specialChar)}>
							{BULLET_POINT}
							{RegistrationNLS.PasswordCriteria.Criteria5.t()}
						</Typography>
					</Grid>
				</Box>
			</Stack>

			{/* New password + confirm password — side by side on desktop */}
			<Grid container spacing={1}>
				<Grid item xs={12} md={6}>
					<Box onBlur={handlePasswordBlur}>
						<PasswordInput
							required
							name="logonPassword"
							label={nls.NewPasswordLabel.t()}
							value={logonPassword}
							onChange={handlePasswordChange}
							error={!!passwordError}
							helperText={passwordError}
						/>
					</Box>
				</Grid>
				<Grid item xs={12} md={6}>
					<PasswordInput
						required
						name="logonPasswordVerify"
						label={nls.VerifyPasswordLabel.t()}
						value={logonPasswordVerify}
						onChange={handlePasswordVerifyChange}
						error={!!passwordVerifyError}
						helperText={passwordVerifyError}
					/>
				</Grid>
			</Grid>

			<Box>
				<Button
					type="submit"
					variant="contained"
					disabled={!isFormReady || loading}
					fullWidth
					sx={buttonSX}
				>
					{nls.SavePasswordButton.t()}
				</Button>
			</Box>

			{/* Resend code */}
			<Stack spacing={1}>
				<Typography variant="caption" color="grey.500">
					{RegistrationNLS.RegistrationNoCode.t()}
				</Typography>
				<Button variant="outlined" onClick={onResend} disabled={loading} sx={buttonSX}>
					{RegistrationNLS.RegistrationResendCode.t()}
				</Button>
			</Stack>
		</Stack>
	);
};
